<template>
    <section class="attachment-viewer" @wheel="wheeled" ref="attachInternaModal">
        <section class="attachment-viewer--body--content">
            <section class="attachment-viewer--body--content--interactive" ref="containerAttachment">
                <VuePDF :id="pdfFIle" :pdf="fileBlob" :page="page" :scale="(scale / 100)" v-if="fileType.isPfd"
                    intent="display" @loaded="pdfLoaded" ref="pdfAttachment">
                </VuePDF>

                <img :src="imageData" v-else-if="fileType.isImage" ref="imageAttachment"
                    style="max-width: 95vw; max-height: 95vh" />

                <textarea v-else-if="fileType.isText" v-model="textData" style="height:80vh; width: 95vw;"
                    ref="textAttachment">
                </textarea>
            </section>
        </section>
    </section>
</template>
<script charset="utf-8">
import { VuePDF, usePDF } from '@tato30/vue-pdf'
    import attachmentsService from '../../../../services/attachments.service';
    import { createPdfSource } from '@/helpers/pdf.viewer.helper';

export default {
    name: "AttachmentViewer",
    components: {
        VuePDF,
    },
    data() {
        return {
            pdfFIle: null,
            viewer: {
                attachmentId: -1,
                sessionId: -1,
                pointId: -1,
                isInteractive: false,
            },
            fileType: {
                isPfd: false,
                isImage: false,
                isText: false,
            },
            range: 100,
            scaleStep: 10,
            page: 1,
            maxPages: 1,
            scale: 100,
            isFromRange: false,
            fileBlob: undefined,
            imageData: undefined,
            textData: undefined,
            lastPdfPageSize: {
                height: -1,
                width: -1
            },
            isBusy: false,
            isFirstRun: true,
            currentWindowSize: {
                width: -1,
                height: -1,
            },
            firsRescaledPdfValue: 100,
        }
    },
    watch:{
        scale:{
            handler(val){
                this.range = val
            },
            immediate: true
        }
    },
    computed: {
        attachmentId() {
            return parseInt(this.$route.params.attachmentId);
        }
    },
    async mounted() {
        try {
            this.getCurrentSize();
            this.parseRequest();
            window.addEventListener('message', this.receiveParentMessage, false);
            await this.internalFetchAttachment();
            this.notifyParentWindow();
        }
        catch (e) {
            console.log(e);
        }
        finally {
            this.$store.commit("hideLoader");
        }
    },
    beforeUnmount() {
        window.removeEventListener('message', this.receiveParentMessage);
    },
    methods: {
        parseRequest() {
            this.viewer.attachmentId = this.attachmentId;
            this.viewer.sessionId = parseInt(this.$route.query.sessionId);
            this.viewer.pointId = parseInt(this.$route.query.pointId);
            this.viewer.isInteractive = (this.$route.query.isInteractive.toLowerCase() === "true");
        },
        receiveParentMessage(event) {
            if (event.source !== window.parent) {
                return;
            }

            if (event.data?.attachmentViewerCommand === undefined) {
                return;
            }

            switch (event.data.attachmentViewerCommand) {
                case "scaleUp":
                    this.scaleUp();
                    break;
                case "scaleDown":
                    this.scaleDown();
                    break;
                case "prevPage":
                    this.prevPage();
                    break;
                case "nextPage":
                    this.nextPage();
                    break;
                case "setScale":
                    this.setScale(event.data.scale);
                    break;
                case "setViewportConstraints":
                    this.setViewportConstraints(event.data.width, event.data.height, event.data.refit === true);
                    break;
                case "setPage":
                    this.setPage(event.data.page);
                    break;
                case "scrollBy":
                    this.scrollBy(event.data.delta);
                    break;
            }
        },
        async notifyParentWindow() {
            try {
                if (this.fileType.isImage) {
                    const img = new Image();
                    img.src = this.imageData;
                    img.onload = () => {
                        this.sentDataToParent({ width: img.width, height: img.height });
                        this.notifyParentState();
                    }
                }
                else if (this.fileType.isText) {
                    this.sentDataToParent({ width: window.parent.innerWidth * 0.45, height: window.parent.innerHeight * 0.80 });
                    this.notifyParentState();
                }

            }
            catch (e) {
                console.log(e);
            }
        },
        async sentDataToParent(data) {
            if (window.parent !== undefined && window.parent !== null) {
                window.parent.postMessage(data);
            }
        },
        notifyParentState() {
            this.sentDataToParent({
                attachmentViewerState: {
                    isPdf: this.fileType.isPfd,
                    isImage: this.fileType.isImage,
                    isText: this.fileType.isText,
                    isReady: this.fileType.isPfd ? this.isFirstRun === false : true,
                    scale: this.scale,
                    page: this.page,
                    maxPages: this.maxPages,
                    minScale: this.firsRescaledPdfValue,
                    maxScale: 250,
                    isBusy: this.isBusy,
                }
            });
        },
        async internalFetchAttachment() {
            let response = await attachmentsService.downloadAttachment(this.viewer.pointId, this.viewer.attachmentId);
            this.resolveAttachmentType(response);
            await this.resolveFile(response);

        },
        resolveAttachmentType(response) {
            if (response.headers['content-type'] === 'application/pdf') {
                this.fileType.isPfd = true;
                this.fileType.isImage = false;
                this.fileType.isText = false;
            }
            else if (response.headers['content-type'].startsWith("image/")) {
                this.fileType.isPfd = false;
                this.fileType.isImage = true;
                this.fileType.isText = false;
            }
            else if (response.headers['content-type'].startsWith("text/plain")) {
                this.fileType.isPfd = false;
                this.fileType.isImage = false;
                this.fileType.isText = true;
            }
        },
        async resolveFile(response) {
            if (this.fileType.isPfd) {
                const { pdf, pages } = usePDF(createPdfSource(response.data));
                this.fileBlob = pdf;
                this.maxPages = pages;
            }
            else if (this.fileType.isImage) {
                let blob = new Blob([response.data], { type: response.headers['content-type'] });
                let urlCreator = window.URL || window.webkitURL;
                this.imageData = await urlCreator.createObjectURL(blob);
            }
            else if (this.fileType.isText) {
                let blob = new Blob([response.data], { type: response.headers['content-type'] });
                this.textData = await blob.text()
            }
        },
        getCurrentSize() {
            this.currentWindowSize.width = (window.parent.innerWidth * 0.9) - 96;
            this.currentWindowSize.height = window.parent.innerHeight * 0.9;
        },
        setViewportConstraints(width, height, refit = false) {
            if (Number.isFinite(width) && width > 0) {
                this.currentWindowSize.width = parseInt(width);
            }

            if (Number.isFinite(height) && height > 0) {
                this.currentWindowSize.height = parseInt(height);
            }

            if (refit === true && this.fileType.isPfd && this.lastPdfPageSize.height > 0 && this.lastPdfPageSize.width > 0) {
                this.reScalePdf();
                this.emitCurrentContentSize({ force: false });
                this.notifyParentState();
            }
        },
        reScalePdf() {
            const maxWidth = this.currentWindowSize.width;
            const maxHeight = this.currentWindowSize.height;

            if (maxWidth <= 0 || maxHeight <= 0 || this.lastPdfPageSize.width <= 0 || this.lastPdfPageSize.height <= 0) {
                return;
            }

            const widthScale = maxWidth / this.lastPdfPageSize.width;
            const heightScale = maxHeight / this.lastPdfPageSize.height;
            const fitScale = Math.min(widthScale, heightScale);

            this.scale = Math.max(10, Math.round(fitScale * 100));
            this.firsRescaledPdfValue = this.scale;
            this.range = this.firsRescaledPdfValue;
        },
        getScaledContentSize(scale = this.scale) {
            const internalScale = scale / 100;

            return {
                width: parseInt(this.lastPdfPageSize.width * internalScale),
                height: parseInt(this.lastPdfPageSize.height * internalScale),
            };
        },
        emitCurrentContentSize(additionalData = {}) {
            if (this.fileType.isPfd === false || this.lastPdfPageSize.width <= 0 || this.lastPdfPageSize.height <= 0) {
                return;
            }

            const scaledSize = this.getScaledContentSize();

            this.sentDataToParent({
                width: scaledSize.width,
                height: scaledSize.height,
                page: this.page,
                scale: this.scale,
                ...additionalData,
            });
        },
        pdfLoaded(e) {
            this.isBusy = false;
            
            if (this.isFirstRun) {
                this.lastPdfPageSize = e;
                this.reScalePdf();
                this.isFirstRun = false;
                this.emitCurrentContentSize({ force: false });
            }

            this.isFirstRun = false;
            this.isFromRange = false;
            this.notifyParentState();
        },
        resetDocumentScrollToTop() {
            this.$nextTick(() => {
                window.scrollTo(0, 0);
                this.scroolView(0);
            });
        },
        prevPage() {
            if (this.isBusy === true) {
                return;
            }

            if (this.page <= 1) {
                return;
            }

            this.isBusy = true;
            this.page--;
            this.resetDocumentScrollToTop();

            this.emitCurrentContentSize({ force: true, prevPage: true, nextPage: undefined });
            this.notifyParentState();
        },

        nextPage() {
            if (this.isBusy === true) {
                return;
            }

            if (this.page >= this.maxPages) {
                return;
            }

            this.isBusy = true;
            this.page++;
            this.resetDocumentScrollToTop();

            this.emitCurrentContentSize({ force: true, prevPage: undefined, nextPage: true });
            this.notifyParentState();
        },

        scaleUp() {
            if (this.isBusy === true) {
                return;
            }

            this.setScale(this.scale + this.scaleStep);
        },

        scaleDown() {
            if (this.isBusy === true) {
                return;
            }

            this.setScale(this.scale - this.scaleStep);
        },

        setScale(scale) {
            if (this.fileType.isPfd === false) {
                return;
            }

            const parsedScale = parseInt(scale);

            if (Number.isNaN(parsedScale)) {
                return;
            }

            this.isBusy = true;
            this.isFromRange = true;
            this.scale = parsedScale;

            if (this.scale >= 250) {
                this.scale = 250;
                this.isBusy = false;
            }

            if (this.scale <= this.firsRescaledPdfValue) {
                this.scale = this.firsRescaledPdfValue;
                this.isBusy = false;
            }

            this.emitCurrentContentSize({ force: true });
            this.notifyParentState();
        },

        setPage(page) {
            if (this.fileType.isPfd === false) {
                return;
            }

            const parsedPage = parseInt(page);

            if (Number.isNaN(parsedPage)) {
                return;
            }

            const nextPage = Math.min(Math.max(parsedPage, 1), this.maxPages);

            if (nextPage === this.page) {
                return;
            }

            this.isBusy = true;
            this.page = nextPage;
            this.resetDocumentScrollToTop();

            this.emitCurrentContentSize({ force: true });
            this.notifyParentState();
        },

        scrollBy(delta) {
            if (this.fileType.isPfd === false) {
                return;
            }

            window.scrollBy(0, parseInt(delta) || 0);

            if (this.scale > this.firsRescaledPdfValue) {
                const percent = this.calculateScroolValue();
                this.scroolView(percent);
            }
        },

        scroolView(percent) {
            this.sentDataToParent({ scroolView: percent });
        },

        closeModal() {
            this.sentDataToParent({ closeModal: true });
        },

        calculateScroolValue() {
            const scrollTop = window.scrollY;
            // Get the total scrollable height
            const totalHeight = document.body.scrollHeight;
            // Get the height of the viewport
            const viewportHeight = window.innerHeight;
            // Calculate the total scrollable distance
            const scrollableDistance = totalHeight - viewportHeight;
            // Calculate the scroll percentage
            const scrollPercentage = (scrollTop / scrollableDistance) * 100;
            // Return the scroll percentage
            return Math.round(scrollPercentage);
        },

        wheeled(e) {
            if (!this.fileType.isPfd) {
                return;
            }

            if (this.isBusy === true) {
                return;
            }

            if (this.scale > this.firsRescaledPdfValue) {
                var percent = this.calculateScroolValue();
                this.scroolView(percent);
                return;
            }
                
            if (e.deltaY > 0) {
                this.nextPage();
            }
            else {
                this.prevPage();
            }
        },
        rangeChange(e) {
            e.preventDefault();
            this.setScale(e.target.value);
        }
    }
}
</script>
<style lang="scss">
.attachment-viewer {
    background: transparent;
    display: flex;
    flex-direction: row;
    column-gap: 10px;

    &--body {
        background: transparent;

        &--content {
            flex-grow: 1;
            display: flex;
            flex-direction: column;

            &--interactive {
                display: flex;
                justify-content: center;
                align-items: flex-end;
                // width: calc(100vw - 100px);
                // height: calc(100vh - 50px);
            }

        }

        &--prev,
        &--next {
            z-index: 999;
            background: transparent;
            display: flex;
            align-items: center;
        }

        &--next {
            margin-right: 10px;
        }
    }
}
</style>
